import React, { createContext, useContext, useState } from 'react';
import { Dimensions } from '@99designs/browser';
import { File } from '../types';
import { ZoomLevel, useZoom } from '../useZoom';

interface FileOverlayContextValue {
    // View Size
    setViewSize: (viewSize: Dimensions) => void;
    viewSize: Dimensions;

    // Zoom Controls
    zoomLevel: ZoomLevel;
    zoomIn: () => void;
    zoomOut: () => void;
    canZoomIn: boolean;
    canZoomOut: boolean;
}

interface FileOverlayContextProviderProps {
    file: Pick<File, 'id' | 'height' | 'width'>;
    children: React.ReactNode;
}

const FileOverlayContext = createContext<FileOverlayContextValue>({
    setViewSize: () => undefined,
    viewSize: { width: 0, height: 0 },
    zoomLevel: ZoomLevel.FIT_TO_CONTAINER,
    zoomIn: () => null,
    zoomOut: () => null,
    canZoomIn: false,
    canZoomOut: false,
});

function FileOverlayContextProvider({ file, children }: FileOverlayContextProviderProps) {
    const [viewSize, setViewSize] = useState<Dimensions>({
        width: file.width,
        height: file.height,
    });

    const { zoomLevel, zoomIn, zoomOut, canZoomIn, canZoomOut } = useZoom(
        viewSize,
        {
            width: file.width,
            height: file.height,
        },
        file.id
    );

    return (
        <FileOverlayContext.Provider
            value={{
                setViewSize,
                viewSize,
                zoomLevel,
                zoomIn,
                zoomOut,
                canZoomIn,
                canZoomOut,
            }}
        >
            {children}
        </FileOverlayContext.Provider>
    );
}

export function useFileOverlayContext(): FileOverlayContextValue {
    return useContext(FileOverlayContext);
}

export default FileOverlayContextProvider;
